if (!customElements.get('wls-explore-cards')) {
    const MOBILE_MQ = '(max-width: 767px)';

    class WlsExploreCards extends HTMLElement {
        constructor() {
            super();
            this.classes = {
                activeDot: 'is-active',
                disabledBtn: 'is-disabled',
            };
            this.elements = {
                scroll: null,
                slides: null,
                prev: null,
                next: null,
                dots: null,
            };
            this.scrollTick = null;
            this.mounted = false;
            this.dotHandlers = [];
            this.mediaQuery = null;
            this.bound = {
                onScroll: this.handleScroll.bind(this),
                onKeydown: this.handleKeydown.bind(this),
                onPrev: this.handlePrev.bind(this),
                onNext: this.handleNext.bind(this),
                onMqChange: this.handleMqChange.bind(this),
            };
        }

        connectedCallback() {
            this.initElements();
            if (!this.elements.scroll || !this.elements.slides.length) {
                return;
            }

            this.mediaQuery = window.matchMedia(MOBILE_MQ);
            if (this.mediaQuery.addEventListener) {
                this.mediaQuery.addEventListener('change', this.bound.onMqChange);
            } else if (this.mediaQuery.addListener) {
                this.mediaQuery.addListener(this.bound.onMqChange);
            }

            this.mount();
        }

        disconnectedCallback() {
            if (this.mediaQuery) {
                if (this.mediaQuery.removeEventListener) {
                    this.mediaQuery.removeEventListener('change', this.bound.onMqChange);
                } else if (this.mediaQuery.removeListener) {
                    this.mediaQuery.removeListener(this.bound.onMqChange);
                }
                this.mediaQuery = null;
            }
            this.unmount();
        }

        initElements() {
            this.elements.scroll = this.querySelector('[data-explore-scroll]');
            this.elements.slides = this.querySelectorAll('[data-explore-slide]');
            this.elements.prev = this.querySelector('[data-explore-prev]');
            this.elements.next = this.querySelector('[data-explore-next]');
            this.elements.dots = this.querySelectorAll('[data-explore-dot]');
        }

        isMobile() {
            return window.matchMedia(MOBILE_MQ).matches;
        }

        handleMqChange() {
            this.unmount();
            this.mount();
        }

        mount() {
            this.unmount();

            const scrollEl = this.elements.scroll;
            const slides = this.elements.slides;
            if (!scrollEl || !slides.length) {
                return;
            }
            if (!this.isMobile()) {
                return;
            }

            scrollEl.addEventListener('scroll', this.bound.onScroll, { passive: true });
            if (this.elements.prev) {
                this.elements.prev.addEventListener('click', this.bound.onPrev);
            }
            if (this.elements.next) {
                this.elements.next.addEventListener('click', this.bound.onNext);
            }
            scrollEl.addEventListener('keydown', this.bound.onKeydown);

            this.elements.dots.forEach((dot) => {
                const fn = () => {
                    const idx = parseInt(dot.getAttribute('data-index') || '0', 10);
                    this.goToIndex(idx);
                };
                dot.addEventListener('click', fn);
                this.dotHandlers.push({ dot, fn });
            });

            this.mounted = true;
            this.syncUi();
        }

        unmount() {
            if (!this.mounted) {
                return;
            }

            const scrollEl = this.elements.scroll;
            if (scrollEl) {
                scrollEl.removeEventListener('scroll', this.bound.onScroll);
                scrollEl.removeEventListener('keydown', this.bound.onKeydown);
            }
            if (this.elements.prev) {
                this.elements.prev.removeEventListener('click', this.bound.onPrev);
            }
            if (this.elements.next) {
                this.elements.next.removeEventListener('click', this.bound.onNext);
            }
            this.dotHandlers.forEach(({ dot, fn }) => {
                dot.removeEventListener('click', fn);
            });
            this.dotHandlers = [];

            this.mounted = false;
        }

        activeIndexFromScroll(scrollEl, slides) {
            if (!slides.length) {
                return 0;
            }
            const mid = scrollEl.scrollLeft + scrollEl.clientWidth * 0.35;
            let idx = 0;
            for (let i = 0; i < slides.length; i++) {
                const el = slides[i];
                const left = el.offsetLeft;
                const right = left + el.offsetWidth;
                if (mid >= left && mid < right) {
                    return i;
                }
                if (left <= mid) {
                    idx = i;
                }
            }
            return idx;
        }

        setDots(index) {
            this.elements.dots.forEach((dot, i) => {
                const on = i === index;
                dot.classList.toggle(this.classes.activeDot, on);
                dot.setAttribute('aria-selected', on ? 'true' : 'false');
            });
        }

        setNavButtons(index, slideCount) {
            if (this.elements.prev) {
                const atStart = index <= 0;
                this.elements.prev.classList.toggle(this.classes.disabledBtn, atStart);
                this.elements.prev.setAttribute('aria-disabled', atStart ? 'true' : 'false');
            }
            if (this.elements.next) {
                const atEnd = slideCount < 2 || index >= slideCount - 1;
                this.elements.next.classList.toggle(this.classes.disabledBtn, atEnd);
                this.elements.next.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
            }
        }

        syncUi() {
            if (this.scrollTick) {
                return;
            }
            this.scrollTick = window.requestAnimationFrame(() => {
                this.scrollTick = null;
                const scrollEl = this.elements.scroll;
                const slides = this.elements.slides;
                if (!scrollEl || !slides.length) {
                    return;
                }
                const idx = this.activeIndexFromScroll(scrollEl, slides);
                if (this.elements.dots.length) {
                    this.setDots(idx);
                }
                this.setNavButtons(idx, slides.length);
            });
        }

        handleScroll() {
            this.syncUi();
        }

        goToIndex(i) {
            const slides = this.elements.slides;
            if (!slides[i]) {
                return;
            }
            slides[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }

        handlePrev() {
            const scrollEl = this.elements.scroll;
            const slides = this.elements.slides;
            const i = this.activeIndexFromScroll(scrollEl, slides);
            if (i > 0) {
                this.goToIndex(i - 1);
            }
        }

        handleNext() {
            const scrollEl = this.elements.scroll;
            const slides = this.elements.slides;
            const i = this.activeIndexFromScroll(scrollEl, slides);
            if (i < slides.length - 1) {
                this.goToIndex(i + 1);
            }
        }

        handleKeydown(e) {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                this.handlePrev();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                this.handleNext();
            }
        }
    }

    customElements.define('wls-explore-cards', WlsExploreCards);
}
